import {nextCashDraft} from "../../utils/cashDraft";
import { useState, useEffect, useRef } from "react";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  addRequestActivePayments,
  initRequestPayments,
  removeRequestActivePayments,
  setComponentActive,
  setOrder,
  updateRequestActivePayments,
  updateRequestPayments,
} from "../../reducers/userConfigSlice";

import { Button } from "@delibrandev/utils";
import { CiCalculator1, CiCreditCard1, CiMoneyBill } from "react-icons/ci";
import "./style.css";
import { finalizeOrderDelivery, getPaymentMethods, payOrder, reportDeliveryError } from "../../api/orders";

export const Payment = () => {
  const dispatch = useDispatch();

  const order = useSelector((state) => state.aplicationConfig.userConfig.order);
  const orderTotal = order?.amounts?.total;

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cashDrafts, setCashDrafts] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportText, setReportText] = useState("");

  const textareaRef = useRef(null);
  const requestInFlight = useRef(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [actionError, setActionError] = useState("");
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  const getCorporationData = async () => {
    try {
      const response = await getPaymentMethods();

      if (response && !response.error) {
        setPaymentMethods(response?.data || []);
      }
    } catch (error) {
      setActionError("No se ha recibido confirmación. Compruebe la conexión y el estado del pedido antes de volver a intentarlo.");
    }
  };

  const finalizeOrder = async () => {
    try {
      const payload = { orderId: order.orderId };
      const response = await finalizeOrderDelivery(payload);

      if (response && !response.error) {
        dispatch(setComponentActive("deliveryDone"));
      } else {
        setActionError("No se ha podido completar la entrega. Compruebe el estado del pedido antes de volver a intentarlo.");
      }

      dispatch(initRequestPayments());
    } catch (error) {
      setActionError("No se ha recibido confirmación. Compruebe la conexión y el estado del pedido antes de volver a intentarlo.");
    }
  };

  const handlePayOrder = async (activePayments) => {
    try {
      const selectedPayment = activePayments?.find((payment) => Number(payment.value) > 0);

      const payload = {
        paymentMethodId: selectedPayment?.paymentMethodId,
        customerId: order.Customer.customerId,
        amount: (activePayments || []).reduce((sum, payment) => sum + Number(payment.value || 0), 0),
        orderId: order.orderId,
      };

      const response = await payOrder(payload);

      if (response && !response.error) {
        await finalizeOrder();
      } else {
        setActionError("No se ha podido confirmar el pago. Compruebe el estado del pedido antes de volver a intentarlo.");
      }

      dispatch(initRequestPayments());
    } catch (error) {
      setActionError("No se ha recibido confirmación. Compruebe la conexión y el estado del pedido antes de volver a intentarlo.");
    }
  };

  const handleOrder = async (activePayments = []) => {
    if (requestInFlight.current) return;
    if (order.paymentStatus !== "paid" && !Number.isFinite(orderTotal)) {setActionError("El importe necesita revisión en el TPV antes de cobrar."); return;}
    requestInFlight.current = true;
    setIsSubmittingPayment(true);
    setActionError("");
    if (order.paymentStatus !== "paid") {
      await handlePayOrder(activePayments);
    } else {
      await finalizeOrder();
    }
    requestInFlight.current = false;
    setIsSubmittingPayment(false);
  };

  const showReportModalHandler = () => {
    setReportError("");
    setShowReportModal(true);
  };

  const hideReportModalHandler = () => {
    if (requestInFlight.current) return;
    setShowReportModal(false);
    setReportText("");
  };

  const submitReportHandler = async () => {
    if (requestInFlight.current) return;
    const note = reportText.trim();
    if (!note) {
      setReportError("Explique qué ha ocurrido para reportar la incidencia.");
      textareaRef.current?.focus();
      return;
    }
    if (note.length > 255) {
      setReportError("La observación puede tener como máximo 255 caracteres.");
      return;
    }
    requestInFlight.current = true;
    setIsSubmittingReport(true);
    setReportError("");
    try {
      const response = await reportDeliveryError({ orderId: order.orderId, note });
      if (!response || response.error !== false) {
        setReportError("No se ha podido registrar la incidencia. El pedido no se ha confirmado como devuelto. Compruebe su sesión y vuelva a intentarlo.");
        return;
      }
      dispatch(setOrder({ ...order, reportDetails: note }));
      dispatch(initRequestPayments());
      dispatch(setComponentActive("deliveryFailed"));
    } catch (error) {
      setReportError("No se ha recibido confirmación. Compruebe la conexión y el estado del pedido antes de reintentar; conservamos su observación.");
    } finally {
      requestInFlight.current = false;
      setIsSubmittingReport(false);
    }
  };

  const PaymentsMethod = () => {
    const activePayments = useSelector((state) => state.aplicationConfig.userConfig.request.activePayments);

    const getPaymentValue = (paymentMethodId) => {
      return activePayments.find((payment) => payment.paymentMethodId === paymentMethodId)?.value || 0;
    };

    const updatePayments = (paymentMethodId, value) => {
      dispatch(
        updateRequestActivePayments({
          paymentMethodId,
          value: Number(value) || 0,
        }),
      );
    };

    const togglePayment = (method) => {
      setCashDrafts(current => ({...current, [method.paymentMethodId]: "0"}));
      const isActive = activePayments.some((payment) => payment.paymentMethodId === method.paymentMethodId);

      // Si vuelves a pulsar el mismo, deselecciona
      if (isActive) {
        dispatch(removeRequestActivePayments({ paymentMethodId: method.paymentMethodId }));
        return;
      }

      // Limpiar métodos anteriores
      dispatch(initRequestPayments());

      // Activar únicamente el seleccionado
      dispatch(
        addRequestActivePayments({
          paymentMethodId: method.paymentMethodId,
          value: method.name === "Efectivo" ? 0 : orderTotal,
        }),
      );
    };

    const handleCashKey = (paymentMethodId, key) => {
      const current = cashDrafts[paymentMethodId] ?? String(getPaymentValue(paymentMethodId) || "0");
      const next = nextCashDraft(current, key);
      setCashDrafts(drafts => ({...drafts, [paymentMethodId]: next}));
      updatePayments(paymentMethodId, next);
    };

    const handleCustomIcon = (name) => {
      if (name === "Efectivo") return <CiMoneyBill />;
      if (name === "Bizum") return <CiCalculator1 />;
      return <CiCreditCard1 />;
    };

    const NumericKeyboard = ({ method }) => {
      const value = getPaymentValue(method.paymentMethodId);

      return (
        <div className="numericKeyboard">
          <div className="numericKeyboardDisplay">{cashDrafts[method.paymentMethodId] ?? Number(value || 0).toFixed(2)} €</div>

          <div className="numericKeyboardGrid">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0"].map((key) => (
              <button
                key={key}
                type="button"
                className="numericKeyboardKey"
                onClick={() => handleCashKey(method.paymentMethodId, key)}
              >
                {key}
              </button>
            ))}

            <button
              type="button"
              className="numericKeyboardKey"
              onClick={() => handleCashKey(method.paymentMethodId, "backspace")}
            >
              ←
            </button>

            {/*<button
              type="button"
              className="numericKeyboardKey numericKeyboardKeyClear"
              onClick={() => handleCashKey(method.paymentMethodId, "clear")}
            >
              C
            </button>*/}
          </div>
        </div>
      );
    };

    const Card = ({ method }) => {
      const activeCard = activePayments.some((payment) => payment.paymentMethodId === method.paymentMethodId);

      return (
        <div className="paymentCardContainer">
          <div
            className={`cardUser ${activeCard ? "cardUserSelected" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => togglePayment(method)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                togglePayment(method);
              }
            }}
          >
            <div className="userIcon">{handleCustomIcon(method.name)}</div>
            <div className="username">{method.name}</div>
          </div>
        </div>
      );
    };

    const selectedMethods = paymentMethods.filter((method) =>
      activePayments.some((payment) => payment.paymentMethodId === method.paymentMethodId),
    );

    return (
      <>
        <div className="cartPayments-title">Seleccione el método de pago</div>

        <div className="cartPayments-list">
          {paymentMethods
            .filter((method) => method.isActive === true)
            .map((method) => (
              <Card key={method.paymentMethodId} method={method} />
            ))}
        </div>

        {selectedMethods.length > 0 && (
          <div className="paymentSelectedBlock">
            {selectedMethods.map((method) => (
              <div key={method.paymentMethodId} className="paymentSelectedItem">
                {method.name === "Efectivo" ? (
                  <NumericKeyboard method={method} />
                ) : (
                  <div className="paymentMethodAutoValue">
                    {Number(getPaymentValue(method.paymentMethodId) || 0).toFixed(2)} €
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    if (showReportModal && textareaRef.current) {
      const textLength = reportText.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textLength, textLength);
    }
  }, [showReportModal, reportText]);


  const PaymentAction = () => {
    const paymentState = useSelector((state) => state.aplicationConfig.userConfig.request.payment);
    const activePayments = useSelector((state) => state.aplicationConfig.userConfig.request.activePayments);

    useEffect(() => {
      const received = activePayments.reduce((sum, payment) => Number(sum) + Number(payment.value || 0), 0);
      const missing = orderTotal - received > 0 ? orderTotal - received : 0;
      const change = received - orderTotal > 0 ? received - orderTotal : 0;

      dispatch(
        updateRequestPayments({
          total: orderTotal,
          received,
          missing,
          change,
        }),
      );
    }, [activePayments]);

    return (
      <div className="paymentAction">
        {order.paymentStatus !== "paid" && (
          <div className="paymentInfo">
            <div className="paymentInfoTitle">Cambio</div>
            <div className="paymentInfoValue">{paymentState.change.toFixed(2)} €</div>
          </div>
        )}

        <div className="flex-gap">
          <div style={{ width: "40%" }}>
            <Button text={"Reportar"} icon={null} customClass={"customButtonError w-100"} action={showReportModalHandler} />
          </div>

          <div style={{ width: "60%" }}>
            {order.paymentStatus === "paid" || (Number.isFinite(orderTotal) && (orderTotal === 0 || (paymentState.received > 0 && paymentState.missing === 0))) ? (
              <Button
                text={isSubmittingPayment ? "Confirmando…" : "Entregar pedido"}
                disabled={isSubmittingPayment}
                icon={null}
                customClass={"customButtonGreen w-100"}
                action={() => handleOrder(activePayments)}
              />
            ) : (
              <div className="paymentAlert">Complete el pago para continuar</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (order.paymentStatus !== "paid") {
      getCorporationData();
    }

    dispatch(
      updateRequestPayments({
        total: orderTotal,
        received: 0,
        missing: 0,
        change: 0,
      }),
    );
  }, []);

  return (
    <div className="paymentContainer" aria-busy={isSubmittingPayment}>
      <div className="paymentTotal">
        {order.paymentStatus !== "paid" ? (
          <>
            <div className="paymentTotalTitle">{order.Customer.fullname}</div>
            <div className="paymentTotalValue">{Number.isFinite(orderTotal) ? orderTotal.toFixed(2) + " €" : "Importe pendiente de revisión en TPV"}</div>
          </>
        ) : (
          <>
            <div className="paymentTotalTitle">El pedido ya ha sido pagado</div>
            <div className="paymentTotalValue"></div>
          </>
        )}
      </div>

      {order.paymentStatus !== "paid" && <PaymentsMethod />}

      {actionError && <p role="alert">{actionError}</p>}
      <PaymentAction />

      <div className="paymentBackButton">
        <Button text={"Volver"} icon={null} customClass={"w-100"} action={() => dispatch(setComponentActive("delivery"))} />
      </div>

      {showReportModal && (
        <div className="modal-report-overlay">
          <div className="modal-report-content" role="dialog" aria-modal="true" aria-labelledby="report-title" aria-busy={isSubmittingReport}>
            <div className="modal-report-header">
              <h3 id="report-title">Reportar incidencia de entrega</h3>
            </div>

            <div className="modal-report-body">
              <div className="form-group">
                <textarea
                  ref={textareaRef}
                  id="reportDetails"
                  aria-label="Detalles de la incidencia"
                  disabled={isSubmittingReport}
                  maxLength={255}
                  className="form-control"
                  placeholder="Añada los detalles de lo sucedido"
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                />
              </div>
            </div>

            {reportError && <p role="alert">{reportError}</p>}
            <div className="modal-report-footer">
              <button type="button" className="customButtonCancel h-50" disabled={isSubmittingReport} onClick={hideReportModalHandler}>Cancelar</button>
              <button type="button" className="customButtonError h-50" disabled={isSubmittingReport} onClick={submitReportHandler}>{isSubmittingReport ? "Registrando incidencia…" : "Confirmar incidencia"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
